import {
  Button,
  Drawer,
  Form,
  Input,
  message,
  Modal,
  Select,
  Switch,
  Table,
  Tag,
  Typography,
} from "antd";
import React, { useEffect, useState } from "react";
import { CreateSurveyTemplateRequest } from "../../../constants/apiRequestResponse";
import TEST_IDS from "../../../constants/cypressTestIds";
import {
  SurveyQuestionType,
  SurveyTemplate
} from "../../../constants/types";
import { createSurveyTemplate } from "../../../services/api";
import { Mode } from "./";

interface SurveyTemplateFormProps {
  mode: Mode
  template?: SurveyTemplate;
  onClose: () => void;
  onSuccess: () => void;
}

interface QuestionFormData {
  _id: string;
  statement: string;
  type: SurveyQuestionType;
  category?: string[] | null;
  isPaidOnly?: boolean;
}

export default function SurveyTemplateForm({
  mode,
  template,
  onClose,
  onSuccess,
}: SurveyTemplateFormProps) {
  const [isLoading, setIsLoading] = useState(false);
  const [questions, setQuestions] = useState<QuestionFormData[]>([]);
  const [form] = Form.useForm<{ description: string }>();

  useEffect(() => {
    if (mode === "hidden") return
    if (!template) return

    form.setFieldsValue({ description: template.description || "" });
    setQuestions(
      template.questions?.map((q) => ({
        _id: q._id,
        statement: q.statement,
        type: q.type,
        category: q.category,
        isPaidOnly: q.isPaidOnly,
      })) || []
    );
  }, [template, form, mode]);

  const handleClose = () => {
    form.resetFields();
    setQuestions([]);
    onClose();
  };

  // Collect all unique categories from all questions for Select options
  const allCategories = [...new Set(questions.flatMap((q) => q.category || []))];

  const columns = [
    {
      title: "#",
      width: 50,
      render: (_: any, __: any, index: number) => index + 1,
    },
    {
      title: "Statement",
      dataIndex: "statement",
      ellipsis: true,
    },
    {
      title: "Type",
      dataIndex: "type",
      width: 60,
    },
    {
      title: "Category",
      dataIndex: "category",
      width: 300,
      render: (value: string[], record: QuestionFormData) => {
        if (mode === "view") {
          return value?.length ? (
            <span>{value.map((c) => <Tag key={c}>{c}</Tag>)}</span>
          ) : (
            <Typography.Text type="secondary">-</Typography.Text>
          );
        } else if (mode === "create") {
          return (
            <Select
              mode="tags"
              value={value || []}
              options={allCategories.map((c) => ({ label: c, value: c }))}
              onChange={(val) =>
                setQuestions((prev) =>
                  prev.map((q) => (q._id === record._id ? { ...q, category: val } : q))
                )
              }
              style={{ width: "100%" }}
              placeholder="Add categories"
            />
          );
        }
      },
    },
    {
      title: "Paid Only",
      dataIndex: "isPaidOnly",
      width: 90,
      render: (value: boolean | undefined, record: QuestionFormData) => (
        <Switch
          checked={value || false}
          disabled={mode !== "create"}
          onChange={(checked) =>
            setQuestions((prev) =>
              prev.map((q) => (q._id === record._id ? { ...q, isPaidOnly: checked } : q))
            )
          }
        />
      ),
    },
  ];

  return (
    <Drawer
      title={mode === "view"
        ? `View Template: ${template?.name || "Template"}`
        : mode === "create" ?
          `Create a new version of ${template?.name?.toLowerCase() || "Template"}` : null}
      placement="right"
      visible={mode !== "hidden"}
      onClose={handleClose}
      width={1000}
      destroyOnClose
    >
      <Form
        form={form}
        layout="vertical"
        onFinish={(formValues) => {
          if (!template || mode !== "create") return;

          Modal.confirm({
            title: "Confirm",
            content: "This will deprecate the current version. All survey campaigns from here on will use the new version.",
            okText: "Create",
            cancelText: "Cancel",
            onOk: async () => {
              try {
                setIsLoading(true);

                const req: CreateSurveyTemplateRequest = {
                  description: formValues.description,
                  copyFromSurveyTemplateId: template._id,
                  questions: template.questions.map((originalQ) => {
                    const editedQ = questions.find((q) => q._id === originalQ._id);
                    return {
                      _id: originalQ._id,
                      category: editedQ?.category ?? originalQ.category,
                      isPaidOnly: editedQ?.isPaidOnly ?? originalQ.isPaidOnly,
                    };
                  }),
                };

                await createSurveyTemplate(req);
                message.success("New version created successfully. It is now the active version.");
                handleClose();
                onSuccess();
              } catch (err) {
                message.error(err?.toString() || "An error occurred");
              } finally {
                setIsLoading(false);
              }
            },
          });
        }}>
        <Form.Item label="Description" name="description">
          <Input.TextArea
            rows={2}
            placeholder="Enter a description for this template"
            disabled={mode === "view"}
            data-testid={TEST_IDS.ADMIN_SURVEY_TEMPLATE_FORM.description_input}
          />
        </Form.Item>

        <div style={{ marginBottom: 16 }}>
          <Typography.Text strong style={{ display: "block", marginBottom: 8 }}>
            Questions ({questions.length})
          </Typography.Text>
          {mode === "view" && (
            <Typography.Text type="secondary" style={{ display: "block", marginBottom: 16 }}>
              You can modify the categories and paid-only status for each question below.
            </Typography.Text>
          )}

          <Table
            rowKey="_id"
            dataSource={questions}
            columns={columns}
            pagination={false}
            size="small"
          />
        </div>

        <div style={{ display: "flex", justifyContent: "flex-end", gap: 8 }}>
          <Button
            onClick={handleClose}
            data-testid={TEST_IDS.ADMIN_SURVEY_TEMPLATE_FORM.close_btn}
          >
            {mode === "view" ? "Close" : "Cancel"}
          </Button>
          {mode === "create" && (
            <Button
              type="primary"
              htmlType="submit"
              loading={isLoading}
              data-testid={TEST_IDS.ADMIN_SURVEY_TEMPLATE_FORM.save_btn}
            >
              Save
            </Button>
          )}
        </div>
      </Form>
    </Drawer >
  );
}
